// src/views/DeviceConfig/TabAnimations.js
import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Form, Input, Card, Space, Button, Switch, Slider, Typography, Tooltip, Image } from 'antd';
import { ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Player } from '@lottiefiles/react-lottie-player';
import axios from '../../../config/axiosInstance';
import { useAuth } from '../../../context/AuthContext';

// Defaults (local assets)
import success from '../../../assets/animations/success.json';
import printing from '../../../assets/animations/printing.json';
import waiting from '../../../assets/animations/scan_qrcode.json';
import denied from '../../../assets/animations/denied.json';
import gear from '../../../assets/animations/gear.json';

const { Text } = Typography;

// ---- helpers ---------------------------------------------------------------
// URL points to a static/animated image instead of a Lottie JSON
const isImageUrl = (v) => /\.(png|gif)$/i.test(v || '');

// sanitize and validate remote URLs (must be http(s); .json always allowed,
// .png/.gif only with the design feature)
const isValidAnimationUrl = (v, allowImages) => {
    if (!v) return true;                      // allow empty => fallback to default
    try {
        const u = new URL(v);
        if (!['http:', 'https:'].includes(u.protocol)) return false;
        const path = u.pathname.toLowerCase();
        if (path.endsWith('.json')) return true;
        return allowImages && (path.endsWith('.png') || path.endsWith('.gif'));
    } catch {
        return false;
    }
};

// A small card with input + live preview. Fallback chain of the preview mirrors
// the delivery to the devices: own URL -> client default -> built-in default.
// With the design feature each phase has its own on/off switch on top.
const AnimationItem = ({ form, fieldName, phaseKey, label, help, defaultJson, clientDefault, allowImages }) => {
    const value = Form.useWatch(fieldName, form);
    const phaseEnabled = Form.useWatch(['PhaseAnimationsEnabled', phaseKey], form) !== false;

    const { src, source } = useMemo(() => {
        if (value && isValidAnimationUrl(value, allowImages)) return { src: value, source: 'remote' };
        if (clientDefault) return { src: clientDefault, source: 'client default' };
        return { src: defaultJson, source: 'default' };
    }, [value, clientDefault, defaultJson, allowImages]);

    const showImagePreview = typeof src === 'string' && isImageUrl(src);

    const resetToDefault = () => {
        form.setFieldsValue({ [fieldName]: '' });
    };

    return (
        <Card
            size="small"
            style={{ height: '100%' }}
            title={
                <Space align="center">
                    {label}
                    <Tooltip title={help}>
                        <InfoCircleOutlined />
                    </Tooltip>
                </Space>
            }
            extra={
                <Button icon={<ReloadOutlined />} onClick={resetToDefault}>
                    Reset
                </Button>
            }
        >
            {allowImages && (
                <Form.Item
                    name={['PhaseAnimationsEnabled', phaseKey]}
                    valuePropName="checked"
                    initialValue={true}
                    label={
                        <>
                            Animation enabled
                            <Tooltip title="Show the animation on this phase. Off = text only on this phase.">
                                <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                            </Tooltip>
                        </>
                    }
                >
                    <Switch />
                </Form.Item>
            )}

            {(!allowImages || phaseEnabled) && (
            <>
            <Form.Item
                name={fieldName}
                label={allowImages ? 'Animation URL' : 'Lottie JSON URL'}
                tooltip={allowImages
                    ? 'Paste a direct URL to a Lottie .json, .png or .gif file (optional). If left blank, the client default or the built-in default animation is used.'
                    : 'Paste a direct URL to a Lottie .json file (optional). If left blank, the default animation will be used.'}
                rules={[
                    {
                        validator: (_, val) =>
                            isValidAnimationUrl(val, allowImages)
                                ? Promise.resolve()
                                : Promise.reject(new Error(allowImages
                                    ? 'It must be an http(s) URL ending in .json, .png or .gif.'
                                    : 'It must be an http(s) URL ending in .json.')),
                    },
                ]}
            >
                <Input placeholder={allowImages ? 'https://.../animation.json|.png|.gif ' : 'https://.../animation.json '} />
            </Form.Item>

            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
                {showImagePreview ? (
                    <Image src={src} alt="preview" height={220} style={{ maxWidth: 220, objectFit: 'contain' }} />
                ) : (
                    /* the Player accepts either a URL string or a JSON object */
                    <Player autoplay loop src={src} style={{ width: 220, height: 220 }} />
                )}
            </div>

            <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Text type="secondary">{`Preview (${source})`}</Text>
            </div>
            </>
            )}
        </Card>
    );
};

const TabAnimations = () => {
    // grab the parent form instance
    const form = Form.useFormInstance();
    const { hasFeature, isAdmin } = useAuth?.() || { hasFeature: () => false, isAdmin: false };

    // Animation customisation (on/off, size, fullscreen, PNG/GIF) is a paid client feature
    const showDesign = hasFeature('deviceConfig.design') || isAdmin;

    // With animations switched off everything else in this tab is hidden
    // (undefined counts as on, the schema default is true)
    const animationsEnabled = Form.useWatch('IsLottiesAnimationsEnabled', form) !== false;
    const showAnimationDetails = !showDesign || animationsEnabled;

    // Client defaults for the previews: in the admin list clientID is populated
    // with its defaultAnimations, otherwise the own client's defaults are fetched.
    const clientFromConfig = Form.useWatch('clientID', form);
    const [ownDefaults, setOwnDefaults] = useState({});

    useEffect(() => {
        if (!showDesign) return;
        if (clientFromConfig && typeof clientFromConfig === 'object') return;

        axios.get('/clients/design/default-animations')
            .then(({ data }) => setOwnDefaults(data.defaultAnimations || {}))
            .catch(() => setOwnDefaults({}));
    }, [showDesign, clientFromConfig]);

    const clientDefaults = showDesign
        ? ((clientFromConfig && typeof clientFromConfig === 'object'
            ? clientFromConfig.defaultAnimations
            : ownDefaults) || {})
        : {};

    return (
        <>
            {/* Animation behaviour (design feature only) */}
            {showDesign && (
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    {/* Columns mirror the animation cards below (Waiting/Checking/...) */}
                    <Col xs={24} md={12} lg={8}>
                        <Form.Item
                            name="IsLottiesAnimationsEnabled"
                            valuePropName="checked"
                            initialValue={true}
                            label={
                                <>
                                    Animations enabled
                                    <Tooltip title="Show the phase animations on the check-in screen. Off = texts only.">
                                        <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                                    </Tooltip>
                                </>
                            }
                        >
                            <Switch />
                        </Form.Item>
                    </Col>

                    {animationsEnabled && (
                        <Col xs={24} md={12} lg={8}>
                            <Form.Item
                                name="AnimationSize"
                                initialValue={25}
                                label={
                                    <>
                                        Animation size
                                        <Tooltip title="Size of the phase animation as percentage of the screen. 100% shows the animation fullscreen on top of the whole screen.">
                                            <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                                        </Tooltip>
                                    </>
                                }
                            >
                                <Slider
                                    min={10}
                                    max={100}
                                    tooltip={{ formatter: (value) => (value >= 100 ? 'Fullscreen' : `${value}%`) }}
                                    style={{ marginInline: 0 }}
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
            )}

            {showAnimationDetails && (
            <Row gutter={[16, 16]}>
            <Col xs={24} md={12} lg={8}>
                <AnimationItem
                    form={form}
                    fieldName="LottieWaitingUrl"
                    phaseKey="waiting"
                    label="Waiting"
                    help="Standby screen/display QR."
                    defaultJson={waiting}
                    clientDefault={clientDefaults.waiting}
                    allowImages={showDesign}
                />
            </Col>

            <Col xs={24} md={12} lg={8}>
                <AnimationItem
                    form={form}
                    fieldName="LottieCheckingUrl"
                    phaseKey="checking"
                    label="Checking"
                    help="Checking permissions/entry."
                    defaultJson={gear}
                    clientDefault={clientDefaults.checking}
                    allowImages={showDesign}
                />
            </Col>

            <Col xs={24} md={12} lg={8}>
                <AnimationItem
                    form={form}
                    fieldName="LottiePrintingUrl"
                    phaseKey="printing"
                    label="Printing"
                    help="Printing badge."
                    defaultJson={printing}
                    clientDefault={clientDefaults.printing}
                    allowImages={showDesign}
                />
            </Col>

            <Col xs={24} md={12} lg={8}>
                <AnimationItem
                    form={form}
                    fieldName="LottieSuccessUrl"
                    phaseKey="success"
                    label="Success"
                    help="Success"
                    defaultJson={success}
                    clientDefault={clientDefaults.success}
                    allowImages={showDesign}
                />
            </Col>

            <Col xs={24} md={12} lg={8}>
                <AnimationItem
                    form={form}
                    fieldName="LottieDeniedUrl"
                    phaseKey="denied"
                    label="Denied"
                    help="Access denied/blacklisted/error."
                    defaultJson={denied}
                    clientDefault={clientDefaults.denied}
                    allowImages={showDesign}
                />
            </Col>
            </Row>
            )}
        </>
    );
};

export default TabAnimations;
